import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';

import Configs from '@/Configs';
import { fetchOptions, handleResponse } from '@/common/apiCalls';
import { canonicalizeCompanyId } from '@/common/canonicalizeCompanyId';
import type { ContextProfile } from '@/common/dataContext';
import {
  canonicalizeEmployeeId,
  persistSessionProfileKeys,
} from '@/common/sessionProfileCache';
import { queriesKeys } from '@/queries/queries-keys';
import { useBasicQueryParams } from '@/queries/useBasicQueryParams';

export type GetSessionProfileQueryConfig = Omit<
  UseQueryOptions<ContextProfile, Error, ContextProfile>,
  'queryKey' | 'queryFn'
> & {
  /** Override auth company when seeding from session before auth ids settle. */
  companyIDOverride?: string | number | null;
  /** Override auth employee when seeding from session before auth ids settle. */
  employeeIDOverride?: string | number | null;
};

/**
 * Authenticated user's session self-profile for DataProvider / App shell
 * (`GET platformGetProfileAPI` full body).
 *
 * Uses a dedicated `sessionSelfProfile` key — do not share with Career or organigram hooks.
 * Default `staleTime: Infinity` (session-static); seed via `initialData` from sessionStorage.
 */
export function useGetSessionProfile(
  queryConfig: GetSessionProfileQueryConfig = {}
): UseQueryResult<ContextProfile, Error> {
  const { companyID, employeeID, token, correlationId, basicEnabled } = useBasicQueryParams();
  const {
    enabled = true,
    staleTime = Infinity,
    companyIDOverride,
    employeeIDOverride,
    ...restQueryConfig
  } = queryConfig;

  const canonicalCompanyID =
    canonicalizeCompanyId(companyIDOverride) ?? canonicalizeCompanyId(companyID);
  const canonicalEmployeeID =
    canonicalizeEmployeeId(employeeIDOverride) ?? canonicalizeEmployeeId(employeeID);

  return useQuery<ContextProfile, Error>({
    ...restQueryConfig,
    staleTime,
    enabled:
      enabled &&
      basicEnabled &&
      canonicalCompanyID != null &&
      canonicalEmployeeID != null,
    queryKey: queriesKeys.sessionSelfProfile(canonicalCompanyID, canonicalEmployeeID),
    queryFn: async () => {
      const qs = new URLSearchParams();
      qs.set('companyID', String(canonicalCompanyID));
      qs.set('employeeID', String(canonicalEmployeeID));

      const response = await fetch(
        `${Configs.platformGetProfileAPI}?${qs.toString()}`,
        fetchOptions('GET', null, token, 'json', correlationId)
      );

      const result = await handleResponse(response);

      if (typeof result === 'number') {
        throw new Error(`sessionSelfProfile:${result}`);
      }

      if (result == null || typeof result !== 'object' || Array.isArray(result)) {
        throw new Error('sessionSelfProfile: unexpected response shape');
      }

      const profile = result as ContextProfile;
      // Keep legacy session fragment readers in sync when Query hydrates without getProfile.
      persistSessionProfileKeys(profile);
      return profile;
    },
  });
}
